import { AppointmentStatus, Timezone } from '@bb/domain/dist/types';
import { Link, Space, Text, theme } from 'components';
import React from 'react';
import styled from 'styled-components';
import { formatDateStrWithYearAndDayNoOrd, formatTimeStr } from 'utils';
import { TIME_CHANGE_STATUSES } from '../../constants';
import ListingImage from './ListingImage';
import DotOrCheckbox from './DotOrCheckbox';

export interface ListingEntryProps {
  line1: string;
  line2?: string;
  listingId: string;
  needsAction?: boolean;
  thumbnail?: string;
  time?: string;
  timeEnd?: string;
  timezone?: Timezone;
  type?: 'modal';
  isMobile?: boolean;
  originalTime?: string;
  originalTimeEnd?: string;
  status?: AppointmentStatus;
  tourName?: string;
  passed?: boolean;
  appointmentId?: string;
}

const timeToLowercase = (time: string) =>
  time.replace(' AM', 'am').replace(' PM', 'pm');

const ListingEntry: React.FC<ListingEntryProps> = ({
  line1,
  line2 = '',
  needsAction,
  time,
  timeEnd,
  timezone,
  thumbnail,
  type,
  listingId,
  isMobile,
  originalTime,
  originalTimeEnd,
  status,
  tourName,
  passed,
  appointmentId,
}) => {
  const [startTime, endTime] =
    !tourName || !TIME_CHANGE_STATUSES.includes(status) || !originalTime
      ? [time, timeEnd]
      : [originalTime, originalTimeEnd];
  const timeStr = `${timeToLowercase(
    formatTimeStr(startTime, timezone),
  )} - ${timeToLowercase(formatTimeStr(endTime, timezone))}`;
  const dateStr = formatDateStrWithYearAndDayNoOrd(startTime, timezone);

  return (
    <StyledSpace
      key={`${listingId}-${time}`}
      align="center"
      size={12}
      data-cy="listing-entry-main"
    >
      {type !== 'modal' && (
        <DotOrCheckbox
          appointmentId={appointmentId}
          isMobile={isMobile}
          dotVisible={needsAction}
          status={status}
          passed={passed}
        />
      )}
      <ListingImage thumbnail={thumbnail} />
      <StyledTextContainer>
        {type === 'modal' ? (
          <Text
            data-cy="listing-entry-address"
            color="textPrimary"
          >{`${line1} ${line2 || ''}`}</Text>
        ) : (
          <Link
            onClick={e => {
              e.stopPropagation();
            }}
            href={`/#/listing/${listingId}/view`}
            color="textPrimary"
            underlineOnHover
            strong={needsAction}
            data-cy="listing-entry-link"
          >
            {`${line1} ${line2 || ''}`}
          </Link>
        )}
        {time ? (
          <Text color={theme.gray8} data-cy="listing-entry-time">
            {type === 'modal' && `${dateStr}  • `}
            {timeStr}
          </Text>
        ) : (
          <Text color={theme.gray8} data-cy="listing-entry-no-time">
            No time selected
          </Text>
        )}
      </StyledTextContainer>
    </StyledSpace>
  );
};

export default ListingEntry;

const StyledTextContainer = styled.div`
  line-height: 1.5;
  display: flex;
  flex-direction: column;
`;

const StyledSpace = styled(Space)`
  & div {
    z-index: 100;
  }
`;
